import { ToolRegistry } from "../agent/tool-registry.js";
import { runMetricAnalysis, SUPPORTED_OPERATIONS } from "../analysis/metrics.js";
import {
  fetchMetrics,
  fetchSpillovers,
  searchLocations,
} from "./portwatch-client.js";

// LLMへ返すサンプル行数。全レコードは Dataset Store に保持し、ヘッダとこの件数の
// サンプルだけをトークン化して送ることでコンテキストの無駄を避ける。
const LLM_SAMPLE_ROWS = 5;

const SEARCH_LOCATIONS = {
  name: "search_locations",
  description:
    "港・チョークポイント（海上要衝）を名称や国名で検索し、portid と scope を特定します。データ取得の最初に使用してください。取扱量（年間入港隻数）の多い順に返します。",
  input_schema: {
    type: "object",
    properties: {
      query: {
        type: "string",
        description: "港名・要衝名・国名の一部（例: Singapore, Suez, Japan）。",
      },
      scope: {
        type: "string",
        enum: ["port", "chokepoint", "any"],
        description: "検索対象。港のみ/要衝のみ/両方。既定は any。",
      },
      limit: { type: "integer", minimum: 1, maximum: 50 },
    },
  },
};

const FETCH_METRICS = {
  name: "fetch_metrics",
  description:
    "指定した portid の日次時系列を取得し、ブラウザ内の Dataset Store へ保存します。返却される datasetId を後続の分析に使用してください。港は portcalls/import/export、チョークポイントは n_total/capacity が返ります（import/export/capacity は推計トン）。",
  input_schema: {
    type: "object",
    properties: {
      portid: {
        type: "string",
        description: "search_locations が返した portid。",
      },
      scope: {
        type: "string",
        enum: ["port", "chokepoint"],
        description: "対象が港かチョークポイントか。",
      },
      days: {
        type: "integer",
        minimum: 1,
        maximum: 1000,
        description: "取得する直近日数（最大1000日。既定365）。",
      },
    },
    required: ["portid", "scope"],
  },
};

const ANALYZE_METRICS = {
  name: "analyze_metrics",
  description:
    "Dataset Store に保存した全レコードを対象に決定論的な分析を行います。summary（件数・最新・平均・最小最大・標準偏差・合計）、trend / anomaly（28日平滑＋年間Zスコアで現在水準・前月比・σ・シグナル）。数値を回答する前に必ずこのツールを使い、サンプル行から全体値を推測しないでください。",
  input_schema: {
    type: "object",
    properties: {
      datasetId: { type: "string", description: "fetch_metrics が返した ID。" },
      operation: {
        type: "string",
        enum: SUPPORTED_OPERATIONS,
        description: "summary / trend / anomaly。",
      },
      metric: {
        type: "string",
        description:
          "対象指標（portcalls/import/export/n_total/capacity）。省略時は全数値指標。",
      },
    },
    required: ["datasetId", "operation"],
  },
};

const INSPECT_DATASET = {
  name: "inspect_dataset",
  description:
    "Dataset Store に保存されたデータの列・件数・期間・サンプル・指定列の distinct 値を確認します。大量の生データを取得せず、分析前の構造確認に使用してください。",
  input_schema: {
    type: "object",
    properties: {
      datasetId: { type: "string" },
      sampleSize: { type: "integer", minimum: 0, maximum: 20 },
      distinctColumn: { type: "string" },
    },
    required: ["datasetId"],
  },
};

const FETCH_SPILLOVERS = {
  name: "fetch_spillovers",
  description:
    "指定した港が混乱した場合の波及リスク（PortWatch Spillover Simulator）を上位件数で取得します。kind=trade は国別の貿易リスク額（USD/日, 業種別）、kind=port は影響先の港の輸送能力リスク（トン/日, 平均通過日数つき）。港の混乱が他国・他港へどう波及するかの深掘りに使います。from_portid をキーにするため港(scope=port)のみ対象で、チョークポイントには基本的にデータがありません。値はモデル推計です。",
  input_schema: {
    type: "object",
    properties: {
      portid: {
        type: "string",
        description: "search_locations が返した港の portid（混乱が起きる港）。",
      },
      kind: {
        type: "string",
        enum: ["trade", "port"],
        description:
          "trade=国別の貿易リスク額(USD)、port=影響先の港の輸送能力リスク(トン)。既定 trade。",
      },
      industry: {
        type: "string",
        description: "kind=trade の業種。既定 'Total'（全業種合計）。",
      },
      by: {
        type: "string",
        enum: ["export", "import"],
        description: "kind=trade の並び順の基準。既定 export。",
      },
      limit: {
        type: "integer",
        minimum: 1,
        maximum: 50,
        description: "上位件数。既定10。",
      },
    },
    required: ["portid"],
  },
};

/** PortWatch 用のツールを登録した ToolRegistry を作る。 */
export function createPortwatchToolRegistry(datasetStore) {
  return new ToolRegistry()
    .register(SEARCH_LOCATIONS, (input, context) =>
      searchLocations(input, context),
    )
    .register(FETCH_METRICS, async (input, context) => {
      const { portid, scope, portname, records } = await fetchMetrics(
        input,
        context,
      );
      const columns = [
        ...new Set(records.flatMap((r) => Object.keys(r))),
      ];
      const stored = datasetStore.add({
        source: "portwatch",
        portid,
        scope,
        title: `${portname}（${scope === "port" ? "港" : "チョークポイント"}）`,
        columns,
        records,
      });

      // LLMには全レコードを返さず、識別子と要約だけを返す。
      return {
        datasetId: stored.id,
        portid,
        scope,
        title: stored.title,
        recordCount: records.length,
        columns,
        dateRange: stored.dateRange,
        sample: records.slice(0, LLM_SAMPLE_ROWS),
      };
    })
    .register(ANALYZE_METRICS, (input) => {
      const dataset = datasetStore.get(input.datasetId);
      const result = runMetricAnalysis({
        records: dataset.records,
        operation: input.operation,
        metric: input.metric,
      });
      return {
        datasetId: dataset.id,
        title: dataset.title,
        ...result,
      };
    })
    .register(INSPECT_DATASET, (input) =>
      datasetStore.inspect(input.datasetId, input),
    )
    .register(FETCH_SPILLOVERS, (input, context) =>
      fetchSpillovers(input, context),
    );
}
